// src/pages/StudyWorkflowPage.js - 연구 워크플로우 (심플 카드)

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faSpinner,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { AlertModal, IconSpecial, Toast } from "../components/ui";
import WorkspaceLayout from "../components/WorkspaceLayout";
import UpgradeModal from "../components/UpgradeModal";
import { useAppContext } from "../context/AppContext";
import { useB2bTokenQuota } from "../features/persona/hooks/useB2bTokenQuota";
import { shouldBlockTokenGatedStudyFeature } from "../utils/productAccess";
import "./StudyWorkflowPage.scss";
import { usePageHeader } from "../context/PageHeaderContext";
import B2BStudyWorkflowPage from "./b2b/B2BStudyWorkflowPage";
import { useB2BMode } from "../hooks/useB2BMode";
import { apiFetch } from "../services/apiClient";
import { getSupportedGuidelineMethodologies } from "../utils/guidelineMethodologies";
import { useStudyFocusGeneration } from "../features/research/hooks/useStudyFocusGeneration";

const StudyWorkflowPage = () => {
  const { studySlug } = useParams(); // slug로 변경
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasMinimumTier } = useAppContext();
  const { isQuotaExceeded } = useB2bTokenQuota();

  // [B2B] 중앙화된 B2B 모드 훅
  const { isB2BMode } = useB2BMode();

  const [study, setStudy] = useState(null);
  const [studyId, setStudyId] = useState(null); // slug로 조회 후 ID 저장
  // null: 아직 상태 미확정(unknown). 빈 배열로 시작하면 "미생성"으로 오해될 수 있음.
  const [artifacts, setArtifacts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studyLoadStatus, setStudyLoadStatus] = useState("loading"); // loading | loaded | not_found | error
  const [studyLoadError, setStudyLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [autoRetryAttempt, setAutoRetryAttempt] = useState(0); // 0..2 (총 3회 시도)
  const [projectName, setProjectName] = useState("");
  const [projectSlug, setProjectSlug] = useState(null);
  const [, setSchedule] = useState(null);
  // 2차 데이터(아티팩트/일정) 로딩 게이트
  const [dataLoading, setDataLoading] = useState(false);
  // 아티팩트 로드 재시도 카운터 (백그라운드 재시도용)
  const [artifactsRetryAttempt, setArtifactsRetryAttempt] = useState(0); // 0..2 (총 3회 시도)
  // 초기 진입 시 커서 hover로 첫 카드가 "툭" 올라오는 현상 방지
  const [hoverEnabled, setHoverEnabled] = useState(false);

  // 연구명 수정 상태

  // 스크리너 설문 생성 로딩 상태
  const [isGeneratingSurvey, setIsGeneratingSurvey] = useState(false);

  // (legacy) generationModal: 현재 UI에서 사용하지 않음

  // Alert 모달 상태
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    type: 'success', // 'success' or 'error'
    message: ''
  });

  // Upgrade 모달 상태
  const [upgradeModal, setUpgradeModal] = useState({
    isOpen: false,
    reason: 'feature'
  });

  const [toast, setToast] = useState({ isOpen: false, type: 'success', message: '' });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ isOpen: true, type, message });
  }, []);

  useEffect(() => {
    if (!location.state?.toast?.message) return;
    showToast(location.state.toast.message, location.state.toast.type || 'success');
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, showToast]);

  // Alert 표시 함수
  const showAlert = (type, message) => {
    setAlertModal({
      isOpen: true,
      type,
      message
    });
  };

  const isQuotaExceededResponse = (response) => response?.status === 402;
  const isQuotaExceededError = (error) => error?.status === 402;

  const createResponseError = (response, message) => {
    const error = new Error(message);
    error.status = response?.status;
    return error;
  };

  // 가이드라인 방법론 선택 모달
  const [methodologyModal, setMethodologyModal] = useState({
    isOpen: false,
    extractedMethodologies: [], // 계획서에서 추출된 방법론
    selectedMethodologies: [],
    loading: false,
  });
  const [guidelineMethodologyState, setGuidelineMethodologyState] = useState({
    status: "idle",
    supportedMethodologies: null,
  });

  const closeMethodologyModal = () => {
    setMethodologyModal({
      isOpen: false,
      extractedMethodologies: [],
      selectedMethodologies: [],
      loading: false,
    });
  };

  // 연구명 수정 함수
  const handleStudyNameSave = async (newValue) => {
    if (!newValue || !newValue.trim()) {
      showAlert('error', '연구명을 입력해주세요.');
      return;
    }

    try {
      const response = await apiFetch(`/studies/${studyId}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: newValue.trim()
        }),
      });

      if (response.ok) {
        const result = await response.json();
        const newSlug = result.data?.slug;
        
        // ⭐ slug가 변경되었으면 URL 업데이트
        if (newSlug && newSlug !== studySlug) {
          navigate(`/study/${newSlug}`, { replace: true });
        } else {
          // slug가 변경되지 않았으면 study 정보만 업데이트
          setStudy(prev => ({ ...prev, ...result.data }));
        }
      } else {
        const errorText = await response.text();
        if (isQuotaExceededResponse(response)) {
          throw createResponseError(response, errorText);
        }
        showAlert('error', `연구명 저장 실패: ${errorText}`);
        throw createResponseError(response, errorText);
      }
    } catch (error) {
      console.error('연구명 저장 오류:', error);
      if (isQuotaExceededError(error)) {
        throw error;
      }
      showAlert('error', '연구명 저장 중 오류가 발생했습니다.');
      throw error;
    }
  };

  const handleStudyNameCancel = () => {
    // 취소 시 별도 처리 없음 (PageHeader에서 자동 처리)
  };
  // slug로 스터디 조회
  useEffect(() => {
    const loadStudyBySlug = async () => {
      setLoading(true);
      setStudyLoadStatus("loading");
      setStudyLoadError(null);
      let scheduledRetry = false;
      
      try {
        // slug로 연구 조회
        const studyRes = await apiFetch(`/studies/by-slug/${studySlug}`, { method: "GET" });
        if (studyRes.ok) {
          const studyData = await studyRes.json();
          const studyIdFromData = studyData.id;
          
          setStudy(studyData);
          setStudyId(studyIdFromData); // ID 저장
          setStudyLoadStatus("loaded");
          setAutoRetryAttempt(0);
          
          // URL 정규화: 파라미터가 slug와 다르면 slug로 교체
          if (studyData.slug && studySlug !== studyData.slug) {
            navigate(`/study/${studyData.slug}`, { replace: true });
          }
          
          // 프로젝트 정보와 아티팩트를 병렬로 로드
          const promises = [];
          
          // 프로젝트 정보
          if (studyData.project_id) {
            promises.push(
              apiFetch(`/projects/${studyData.project_id}`, { method: "GET" })
                .then(res => res.ok ? res.json() : null)
                .then(projectData => {
                  if (projectData) {
                    setProjectName(projectData.name || "");
                    setProjectSlug(projectData.slug || null);
                  }
                })
                .catch(err => console.error("프로젝트 로드 오류:", err))
            );
          }
          
          // 아티팩트와 일정을 병렬로 로드 (재시도 로직 포함)
          setDataLoading(true); // 아티팩트 요청 시작 시 로딩 표시
          
          const loadArtifactsWithRetry = async (studyIdForArtifacts, attempt = 0) => {
            try {
              const [artifactsRes, scheduleRes] = await Promise.all([
                apiFetch(`/studies/${studyIdForArtifacts}/artifacts`, { method: "GET" }),
                apiFetch(`/studies/${studyIdForArtifacts}/schedule`, { method: "GET" })
              ]);
              
              // 아티팩트 상태를 먼저 업데이트
              if (artifactsRes.ok) {
                try {
                  const artifactsData = await artifactsRes.json();
                  const artifacts = artifactsData.artifacts || [];
                  setArtifacts(artifacts);
                  setArtifactsRetryAttempt(0); // 성공 시 재시도 카운터 리셋
                } catch (err) {
                  console.error("[ERROR] 아티팩트 파싱 오류:", err);
                  // 파싱 에러는 재시도하지 않음 (데이터 문제)
                  setArtifacts([]);
                }
              } else if (artifactsRes.status === 500) {
                // 500 에러는 서버 문제이므로 재시도
                if (attempt < 2) {
                  const delayMs = attempt === 0 ? 250 : attempt === 1 ? 700 : 1400;
                  console.log(`[RETRY] 아티팩트 로드 실패 (500), ${delayMs}ms 후 재시도 (시도 ${attempt + 1}/3)`);
                  // 재시도 중에는 상태를 변경하지 않음 (깜빡임 방지)
                  setTimeout(() => {
                    setArtifactsRetryAttempt(attempt + 1);
                    loadArtifactsWithRetry(studyIdForArtifacts, attempt + 1);
                  }, delayMs);
                  return; // 재시도 예약했으므로 여기서 종료 (로딩 상태 유지)
                } else {
                  // 3번 시도 후에도 실패하면 빈 배열로 설정
                  console.error("[ERROR] 아티팩트 로드 실패 (500), 3회 시도 후 포기");
                  setArtifacts([]);
                  setTimeout(() => {
                    setDataLoading(false);
                  }, 0);
                  return; // 여기서 종료
                }
              } else {
                // 404, 403 등은 클라이언트 에러이므로 재시도하지 않음
                setArtifacts([]);
              }
              
              if (scheduleRes.ok) {
                try {
                  const scheduleData = await scheduleRes.json();
                  setSchedule(scheduleData.schedule || null);
                } catch (err) {
                  console.error("[ERROR] 일정 파싱 오류:", err);
                }
              }
              
              // 아티팩트 상태 업데이트 후 다음 틱에서 로딩 완료 표시 (상태 업데이트 순서 보장)
              setTimeout(() => {
                setDataLoading(false);
              }, 0);
            } catch (err) {
              console.error("[ERROR] 아티팩트/일정 로드 중 예외 발생:", err);
              // 네트워크 에러 등은 재시도
              if (attempt < 2) {
                const delayMs = attempt === 0 ? 250 : attempt === 1 ? 700 : 1400;
                console.log(`[RETRY] 아티팩트 로드 네트워크 에러, ${delayMs}ms 후 재시도 (시도 ${attempt + 1}/3)`);
                // 재시도 중에는 상태를 변경하지 않음 (깜빡임 방지)
                setTimeout(() => {
                  setArtifactsRetryAttempt(attempt + 1);
                  loadArtifactsWithRetry(studyIdForArtifacts, attempt + 1);
                }, delayMs);
                return; // 재시도 예약했으므로 여기서 종료 (로딩 상태 유지)
              } else {
                // 3번 시도 후에도 실패하면 빈 배열로 설정
                console.error("[ERROR] 아티팩트 로드 네트워크 에러, 3회 시도 후 포기");
                setArtifacts([]);
                setTimeout(() => {
                  setDataLoading(false);
                }, 0);
              }
            }
          };
          
          promises.push(loadArtifactsWithRetry(studyIdFromData, 0));
          
          // 모든 병렬 요청 실행 (완료를 기다리지 않음)
          Promise.all(promises).catch(err => console.error("병렬 로드 오류:", err));
        } else {
          console.error("Study 조회 실패:", studyRes.status);
          // 404는 확정 not_found, 그 외(500/401/403 등)는 "일시 오류 가능" → 3번까지 자동 재시도
          if (studyRes.status !== 404 && autoRetryAttempt < 2) {
            const delayMs = autoRetryAttempt === 0 ? 250 : autoRetryAttempt === 1 ? 700 : 1400;
            scheduledRetry = true;
            window.setTimeout(() => {
              setAutoRetryAttempt((v) => v + 1);
              setReloadKey((v) => v + 1);
            }, delayMs);
          } else {
            setStudy(null);
            setStudyId(null);
            setStudyLoadStatus(studyRes.status === 404 ? "not_found" : "error");
            setStudyLoadError({ status: studyRes.status });
          }
        }
      } catch (err) {
        console.error("Study 로드 오류:", err);
        if (autoRetryAttempt < 2) {
          const delayMs = autoRetryAttempt === 0 ? 250 : autoRetryAttempt === 1 ? 700 : 1400;
          scheduledRetry = true;
          window.setTimeout(() => {
            setAutoRetryAttempt((v) => v + 1);
            setReloadKey((v) => v + 1);
          }, delayMs);
        } else {
          setStudy(null);
          setStudyId(null);
          setStudyLoadStatus("error");
          setStudyLoadError({ error: err });
        }
      } finally {
        // 다음 재시도를 예약한 경우엔 로딩을 유지해서 "오류 화면"으로 깜빡이지 않게 한다.
        if (!scheduledRetry) {
          setLoading(false);
        }
      }
    };

    loadStudyBySlug();
  }, [studySlug, navigate, reloadKey, autoRetryAttempt]);

  const retryLoadStudy = useCallback(() => {
    setAutoRetryAttempt(0);
    setReloadKey((v) => v + 1);
  }, []);

  // studyId가 설정된 후 아티팩트와 일정 폴링 (초기 로드는 study 로드 시점에 수행됨)
  useEffect(() => {
    if (!studyId) return;
    
    // 아티팩트가 아직 로드되지 않았으면 폴링 시작하지 않음 (study 로드 시점에 로드됨)
    if (artifacts === null) {
      return;
    }

    const loadData = async (showSpinner = false) => {
      if (showSpinner) {
        setDataLoading(true);
      }
      
      try {
        // 아티팩트와 일정을 병렬로 로드
        const [artifactsRes, scheduleRes] = await Promise.all([
          apiFetch(`/studies/${studyId}/artifacts`, { method: "GET" }),
          apiFetch(`/studies/${studyId}/schedule`, { method: "GET" })
        ]);
        
        // 모든 데이터가 준비된 후에만 상태 업데이트
        let artifacts = [];
        let scheduleData = null;
        let hasServerError = false;
        
        if (artifactsRes.ok) {
          const artifactsData = await artifactsRes.json();
          artifacts = artifactsData.artifacts || [];
        } else if (artifactsRes.status === 500) {
          // 500 에러는 서버 문제이므로 상태 유지하고 다음 폴링에서 재시도
          console.error(`[ERROR] Artifacts 조회 서버 오류 (500) - study_id=${studyId}, 재시도 예정`);
          hasServerError = true;
        } else {
          // 404, 403 등은 클라이언트 에러이므로 로그만 남기고 빈 배열 유지
          const errorData = await artifactsRes.json().catch(() => ({}));
          console.error(`[ERROR] Artifacts 조회 실패: ${artifactsRes.status}`, errorData);
        }

        if (scheduleRes.ok) {
          const scheduleResData = await scheduleRes.json();
          scheduleData = scheduleResData.schedule || null;
        } else if (scheduleRes.status === 500) {
          // 500 에러는 서버 문제이므로 상태 유지하고 다음 폴링에서 재시도
          console.error(`[ERROR] Schedule 조회 서버 오류 (500) - study_id=${studyId}, 재시도 예정`);
          hasServerError = true;
        } else {
          // 404, 403 등은 클라이언트 에러이므로 로그만 남기고 null 유지
          const errorData = await scheduleRes.json().catch(() => ({}));
          console.error(`[ERROR] Schedule 조회 실패: ${scheduleRes.status}`, errorData);
        }
        
        // 서버 에러가 발생한 경우 상태를 업데이트하지 않고 다음 폴링에서 재시도
        // 정상 응답이 하나라도 있으면 부분 업데이트
        if (!hasServerError || artifactsRes.ok || scheduleRes.ok) {
          // 정상 응답이 있는 경우에만 상태 업데이트
          if (artifactsRes.ok) {
            setArtifacts(artifacts);
          } else if (artifactsRes.status !== 500) {
            // 500이 아닌 경우(404, 403 등)는 빈 배열로 설정하여 "unknown" 상태 방지
            // 500인 경우는 hasServerError=true이므로 상태 유지
            setArtifacts([]);
          }
          if (scheduleRes.ok) {
            setSchedule(scheduleData);
          }
        }
        // hasServerError가 true이고 둘 다 실패한 경우는 상태 유지 (아무것도 하지 않음)
      } catch (err) {
        console.error("[ERROR] 데이터 로드 중 예외 발생:", err);
        // 네트워크 오류 등은 다음 폴링에서 재시도하므로 상태 유지
        // 상태를 초기화하지 않음
      } finally {
        if (showSpinner) {
          setDataLoading(false);
        }
      }
    };

    // 폴링만 시작 (초기 로드는 이미 study 로드 시점에 수행됨)
    // 10초마다 폴링 (계획서 생성 완료 확인 + 이름 갱신)
    const interval = setInterval(() => loadData(false), 10000);
    
    // 일정 저장 이벤트 리스너 추가
    const handleScheduleSaved = (event) => {
      const { studyId: savedStudyId } = event.detail || {};
      // 현재 studyId와 일치하면 데이터 다시 로드
      if (savedStudyId && savedStudyId === studyId) {
        console.log('일정 저장 이벤트 수신, 데이터 다시 로드');
        loadData(true);
      }
    };
    
    window.addEventListener('scheduleSaved', handleScheduleSaved);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('scheduleSaved', handleScheduleSaved);
    };
  }, [studyId, artifacts]); // artifacts를 dependency에 추가하여 로드 완료 후 폴링 시작

  // 아티팩트 상태 확인
  const getArtifactStatus = (type) => {
    if (artifacts == null) return "unknown";
    const artifact = artifacts.find((a) => a.artifact_type === type);
    if (!artifact) {
      return "none"; // ← 삭제된 상태
    }
    return artifact.status === "completed" ? "completed" : "pending";
  };

  const getArtifact = (type) => {
    if (artifacts == null) return null;
    return artifacts.find((a) => a.artifact_type === type);
  };

  const hasNoSupportedGuidelineMethodology =
    guidelineMethodologyState.status === "ready" &&
    Array.isArray(guidelineMethodologyState.supportedMethodologies) &&
    guidelineMethodologyState.supportedMethodologies.length === 0;

  // 카드 클릭 핸들러
  const handleCardClick = (type) => {
    if (type === "guideline" && hasNoSupportedGuidelineMethodology) return;

    const status = getArtifactStatus(type);
    const artifact = getArtifact(type);

    // 상태가 아직 확정되지 않았으면(로딩/네트워크 오류 등) 아무 동작도 하지 않는다.
    if (status === "unknown") return;
  
    if (status === "completed") {
      // 완료된 아티팩트 → 상세 페이지
      navigate(`/study/${study.slug || study.id}/artifact/${artifact.id}?type=${encodeURIComponent(type)}`);
    } else if (status === "pending") {
      // ⭐ pending일 때는 이동 차단
      showAlert('error', `${type === "plan" ? "계획서" : type === "survey" ? "스크리닝 설문" : "가이드라인"} 생성이 진행 중입니다. 완료되면 자동으로 표시됩니다.`);
      // [TODO: 나중에 다시 살릴 코드] 생성 중인 아티팩트 → 상세 페이지 (스트리밍 확인)
      // navigate(`/study/${studyId}/artifact/${artifact.id}`);
    } else {
      // artifact가 없는 경우 (삭제된 경우)
      
      // ⭐ Tier 체크: Free 사용자는 스크리너, 가이드라인 생성 불가
      if (
        (type === "survey" || type === "guideline")
        && shouldBlockTokenGatedStudyFeature(user, { isQuotaExceeded, hasMinimumTier })
      ) {
        setUpgradeModal({ isOpen: true, reason: 'feature' });
        return;
      }
      
      if (type === "plan") {
        handleCreatePlan(); // ← 여기서 새로 만든 함수 호출
      } else if (type === "survey") {
        handleGenerateSurvey();
      } else if (type === "guideline") {
        openMethodologyModal();
      }
    }
  };

  // 방법론 선택 모달 열기 (계획서에서 방법론 추출)
  const openMethodologyModal = async () => {
    const planArtifact = getArtifact("plan");

    if (!planArtifact) {
      showAlert('info', '계획서를 먼저 생성해주세요');
      return;
    }

    // 계획서가 완료되지 않았다면 실행 불가
    if (planArtifact.status !== "completed") {
      showAlert('error', '계획서 생성이 완료되면 이용 가능합니다.');
      return;
    }

    setMethodologyModal({
      isOpen: true,
      extractedMethodologies: [],
      selectedMethodologies: [],
      loading: true,
    });

    try {
      // 계획서에서 방법론 추출
      const response = await apiFetch("/extract-methodologies", {
        method: "POST",
        body: JSON.stringify({
          research_plan: planArtifact.content,
        }),
      });

      if (!response.ok) {
        closeMethodologyModal();
        if (isQuotaExceededResponse(response)) return;
        throw createResponseError(response, "방법론 추출 실패");
      }

      const data = await response.json();
      const supported = getSupportedGuidelineMethodologies(data.methodologies || []);

      if (supported.length === 0) {
        setGuidelineMethodologyState({
          status: "ready",
          supportedMethodologies: [],
        });
        closeMethodologyModal();
        showAlert('error', '해당 리서치에 맞는 가이드라인 생성 방법론이 없습니다.');
        return;
      }

      setMethodologyModal({
        isOpen: true,
        extractedMethodologies: supported,
        selectedMethodologies: supported,
        loading: false,
      });
      setGuidelineMethodologyState({
        status: "ready",
        supportedMethodologies: supported,
      });
    } catch (err) {
      console.error("방법론 추출 오류:", err);
      if (isQuotaExceededError(err)) {
        closeMethodologyModal();
        return;
      }
      // 기본값으로 fallback
      setMethodologyModal({
        isOpen: true,
        extractedMethodologies: ["심층 인터뷰", "사용성 테스트"],
        selectedMethodologies: ["심층 인터뷰"],
        loading: false,
      });
    }
  };

  const handleCreatePlan = async () => {
    try {
      console.log('handleCreatePlan 시작 - studyId:', studyId);
      
      if (!studyId) {
        console.error('studyId가 undefined입니다!');
        showAlert('error', '연구 ID를 찾을 수 없습니다.');
        return;
      }
      
      const studyRes = await apiFetch(`/studies/${studyId}`, { method: "GET" });
      if (!studyRes.ok) {
        showAlert('error', '연구 정보를 가져올 수 없습니다.');
        return;
      }
      
      const studyData = await studyRes.json();

      // formData 구성
      const formData = {
        studyName: studyData.name || "",
        problemDefinition: studyData.initial_input || "",
        methodologies: studyData.methodologies || [],
        targetAudience: studyData.target_audience || "",
        participantCount: parseInt(studyData.participant_count) || 0,
        startDate: studyData.start_date || "",
        endDate: studyData.end_date || "",
        timeline: studyData.timeline || "",
        budget: studyData.budget || "",
        additionalRequirements: studyData.additional_requirements || "",
      }
      
      const response = await apiFetch(`/studies/${studyId}/regenerate-plan`, {
        method: "POST",
        body: JSON.stringify({
          formData: formData,
          projectId: studyData.project_id,
          studyId: studyId,
        }),
      });

      if (!response.ok) {
        if (isQuotaExceededResponse(response)) return;
        throw createResponseError(response, "생성 실패");
      }
  
      const data = await response.json();
  
      if (data.artifact_id) {
        // ⭐ StudyWorkflowPage에 머무르고 아티팩트 리스트만 새로고침
        const artifactsRes = await apiFetch(`/studies/${studyId}/artifacts`, {
          method: "GET",
        });
        if (artifactsRes.ok) {
          const artifactsData = await artifactsRes.json();
          setArtifacts(artifactsData.artifacts || []);
        }
        // [TODO: 나중에 다시 살릴 코드] artifact 상세 페이지로 이동 (스트리밍은 그 페이지에서)
        // navigate(`/study/${studyId}/artifact/${data.artifact_id}`);
      }
    } catch (err) {
      console.error("계획서 생성 오류:", err);
      if (isQuotaExceededError(err)) return;
      showAlert('error', '생성 중 오류가 발생했습니다');
    }
  };
  
  // 스크리너 생성기 (설문 문항) - 바로 artifact 페이지로 이동
  const handleGenerateSurvey = async () => {
    // 중복 실행 방지
    if (isGeneratingSurvey) return;

    try {
      setIsGeneratingSurvey(true); // 로딩 시작

      const planArtifact = getArtifact("plan");

      if (!planArtifact) {
        showAlert('info', '계획서를 먼저 생성해주세요');
        setIsGeneratingSurvey(false);
        return;
      }

      // 계획서가 완료되지 않았다면 실행 불가
      if (planArtifact.status !== "completed") {
        showAlert('error', '계획서 생성이 완료되면 이용 가능합니다.');
        setIsGeneratingSurvey(false);
        return;
      }

      const response = await apiFetch("/survey/create-and-generate", {
        method: "POST",
        body: JSON.stringify({
          study_id: studyId,
          research_plan: planArtifact.content,
        }),
      });

      if (!response.ok) {
        if (isQuotaExceededResponse(response)) {
          setIsGeneratingSurvey(false);
          return;
        }
        throw createResponseError(response, "생성 실패");
      }

      const result = await response.json();

      if (result.success) {
        // ⭐ StudyWorkflowPage에 머무르고 아티팩트 리스트만 새로고침
        const artifactsRes = await apiFetch(`/studies/${studyId}/artifacts`, {
          method: "GET",
        });
        if (artifactsRes.ok) {
          const artifactsData = await artifactsRes.json();
          setArtifacts(artifactsData.artifacts || []);
        }
        setIsGeneratingSurvey(false); // 로딩 해제
        // [TODO: 나중에 다시 살릴 코드] artifact 상세 페이지로 이동
        // navigate(`/study/${studyId}/artifact/${result.artifact_id}`);
      } else {
        showAlert('error', "계획서 생성에 실패했습니다: " + (result.error || "알 수 없는 오류"));
        setIsGeneratingSurvey(false);
      }
    } catch (error) {
      console.error("계획서 생성 오류:", error);
      if (isQuotaExceededError(error)) {
        setIsGeneratingSurvey(false);
        return;
      }
      showAlert('error', '계획서 생성 중 오류가 발생했습니다.');
      setIsGeneratingSurvey(false);
    }
  };

  useStudyFocusGeneration({
    enabled: !isB2BMode,
    locationSearch: location.search,
    studySlug: study?.slug || studySlug,
    artifacts,
    getArtifactStatus,
    openMethodologyModal,
    handleGenerateSurvey,
  });

  // 가이드라인 생성 시작 (artifact 페이지로 이동)
  const handleGenerateGuideline = async () => {
    const methodologies = methodologyModal.selectedMethodologies;

    closeMethodologyModal();

    try {
      const planArtifact = getArtifact("plan");

      if (!planArtifact) {
        showAlert('info', '계획서를 먼저 생성해주세요');
        return;
      }

      // 계획서가 완료되지 않았다면 실행 불가
      if (planArtifact.status !== "completed") {
        showAlert('error', '계획서 생성이 완료되면 이용 가능합니다.');
        return;
      }

      const response = await apiFetch("/guideline/create-and-generate", {
        method: "POST",
        body: JSON.stringify({
          study_id: studyId,
          research_plan: planArtifact.content,
          methodologies: methodologies,
        }),
      });

      if (!response.ok) {
        if (isQuotaExceededResponse(response)) {
          closeMethodologyModal();
          return;
        }
        throw createResponseError(response, "생성 실패");
      }

      const data = await response.json();

      if (data.artifact_id) {
        // ⭐ StudyWorkflowPage에 머무르고 아티팩트 리스트만 새로고침
        const artifactsRes = await apiFetch(`/studies/${studyId}/artifacts`, {
          method: "GET",
        });
        if (artifactsRes.ok) {
          const artifactsData = await artifactsRes.json();
          setArtifacts(artifactsData.artifacts || []);
        }
        // [TODO: 나중에 다시 살릴 코드] artifact 상세 페이지로 이동 (스트리밍은 그 페이지에서)
        // navigate(`/study/${studyId}/artifact/${data.artifact_id}`);
      }
    } catch (err) {
      console.error("가이드라인 생성 오류:", err);
      if (isQuotaExceededError(err)) {
        closeMethodologyModal();
        return;
      }
      showAlert('error', '생성 중 오류가 발생했습니다');
    }
  };

  // hooks는 early return 이전에 항상 호출되어야 함
  usePageHeader(
    {
      title: study?.name || (loading ? "로딩 중..." : "리서치"),
      subtitle: projectName || "",
      showBack: true,
      onBack: () =>
        navigate(
          projectSlug
            ? `/project/${projectSlug}`
            : "/research"
        ),
      actions: study?.created_at ? (
        <div style={{ color: "#9ca3af", fontSize: 14, fontWeight: 500 }}>
          생성일:{" "}
          {new Date(study.created_at).toLocaleDateString("ko-KR")}
        </div>
      ) : null,
      editableTitle: study && !loading
        ? {
            value: study.name || "",
            onSave: handleStudyNameSave,
            onCancel: handleStudyNameCancel,
          }
        : null,
    },
    [isB2BMode, study?.name, study?.created_at, loading, projectName, projectSlug]
  );

  // “호버로 돌아오는 느낌”은 실제 hover라기보다
  // 카드가 처음 렌더되는 순간(혹은 로딩 직후)에 transition/transform이 적용되며 생기는 경우가 많다.
  // → 시간 기준이 아니라 “사용자가 마우스를 한 번 움직인 이후”에만 hover/transition을 활성화한다.
  useEffect(() => {
    if (dataLoading) {
      setHoverEnabled(false);
      return undefined;
    }

    let activated = false;
    const activate = () => {
      if (activated) return;
      activated = true;
      setHoverEnabled(true);
    };

    const onMouseMove = () => activate();
    window.addEventListener("mousemove", onMouseMove, true);

    // 키보드/터치 환경 fallback (마우스가 안 움직일 수 있음)
    const t = setTimeout(() => activate(), 1200);

    return () => {
      window.removeEventListener("mousemove", onMouseMove, true);
      clearTimeout(t);
    };
  }, [dataLoading]);

  // [B2B] B2B 모드면 B2B 전용 페이지로 리다이렉트
  if (isB2BMode) {
    return <B2BStudyWorkflowPage />;
  }

  const Layout = WorkspaceLayout;

  if (loading) {
    return (
      <Layout hideCollapseButton={false} defaultCollapsed={true}>
        <div className="study-workflow-simple">
          <div className="loading">로딩 중...</div>
        </div>
      </Layout>
    );
  }

  if (!study) {
    return (
      <Layout hideCollapseButton={false} defaultCollapsed={true}>
        <div className="study-workflow-simple">
          {studyLoadStatus === "not_found" ? (
            <div className="error">연구를 찾을 수 없습니다.</div>
          ) : (
            <div className="error">
              <div style={{ marginBottom: 10 }}>연구를 불러오지 못했습니다.</div>
              <button type="button" onClick={retryLoadStudy} style={{ marginRight: 10 }}>
                다시 시도
              </button>
              <button type="button" onClick={() => window.location.reload()}>
                새로고침
              </button>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout hideCollapseButton={false} defaultCollapsed={false}>
      {/* 아티팩트 카드 */}
      {dataLoading || artifacts === null ? (
        <div className={`artifacts-grid ${hoverEnabled ? "hover-enabled" : "hover-disabled"}`}>
          <div className="card-status loading" style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '24px' }}>
            <FontAwesomeIcon icon={faSpinner} spin />
            <span style={{ marginLeft: 8 }}>아티팩트 로딩 중...</span>
          </div>
        </div>
      ) : (
      <div className={`artifacts-grid ${hoverEnabled ? "hover-enabled" : "hover-disabled"}`}>
        {/* 1. 계획서 */}
        <div
          className={`artifact-card ${getArtifactStatus("plan")}`}
          onClick={() => handleCardClick("plan")}
        >
          <div className="card-icon">
            <IconSpecial name="researchPlan" />
          </div>
          <div className="card-content">
            <h3>리서치 계획서</h3>
            <p className="card-description">
              목적부터 일정까지, 체계적인<br />
              리서치 초안을 생성합니다.
            </p>
            {getArtifactStatus("plan") === "completed" ? (
              <div className="card-status completed">
                <FontAwesomeIcon icon={faCheck} />
                <span>생성 완료</span>
                <FontAwesomeIcon icon={faArrowRight} className="arrow" />
              </div>
            ) : getArtifactStatus("plan") === "pending" ? (
              <div className="card-status generating">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>생성 중...</span>
              </div>
            ) : getArtifactStatus("plan") === "unknown" ? (
              <div className="card-status generating">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>불러오는 중...</span>
              </div>
            ) : (
              <div className="card-status pending">
                <span>생성하기</span>
                <FontAwesomeIcon icon={faArrowRight} className="arrow" />
              </div>
            )}
          </div>
        </div>
        {/* 2. 스크리너 생성기 */}
        <div
          className={`artifact-card ${getArtifactStatus("survey")}`}
          onClick={() => handleCardClick("survey")}
        >
          <div className="card-icon">
            <IconSpecial name="screeningSurvey" />
          </div>
          <div className="card-content">
          <h3>스크리닝 설문</h3>
            <p className="card-description">
              적합한 참여자를 선별하기 위한<br />
              맞춤형 질문을 설계합니다.
            </p>
            {getArtifactStatus("survey") === "completed" ? (
              <div className="card-status completed">
                <FontAwesomeIcon icon={faCheck} />
                <span>생성 완료</span>
                <FontAwesomeIcon icon={faArrowRight} className="arrow" />
              </div>
            ) : getArtifactStatus("survey") === "pending" || isGeneratingSurvey ? (
              <div className="card-status generating">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>생성 중...</span>
              </div>
            ) : getArtifactStatus("survey") === "unknown" ? (
              <div className="card-status generating">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>불러오는 중...</span>
              </div>
            ) : (
              <div className="card-status pending">
                <span>생성하기</span>
                <FontAwesomeIcon icon={faArrowRight} className="arrow" />
              </div>
            )}
          </div>
        </div>

        {/* 3. 가이드라인 */}
        <div
          className={`artifact-card ${hasNoSupportedGuidelineMethodology ? "disabled" : getArtifactStatus("guideline")}`}
          onClick={() => handleCardClick("guideline")}
        >
          <div className="card-icon">
            <IconSpecial name="guideline" />
          </div>
          <div className="card-content">
            <h3>진행 가이드라인</h3>
            <p className="card-description">
              인터뷰의 질을 높여주는<br />
              인터뷰 스크립트를 제작합니다.
            </p>
            {hasNoSupportedGuidelineMethodology ? (
              <div className="card-status unavailable">
                <span>해당 리서치에 맞는 가이드라인 생성 방법론이 없습니다</span>
              </div>
            ) : getArtifactStatus("guideline") === "completed" ? (
              <div className="card-status completed">
                <FontAwesomeIcon icon={faCheck} />
                <span>생성 완료</span>
                <FontAwesomeIcon icon={faArrowRight} className="arrow" />
              </div>
            ) : getArtifactStatus("guideline") === "pending" ? (
              <div className="card-status generating">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>생성 중...</span>
              </div>
            ) : getArtifactStatus("guideline") === "unknown" ? (
              <div className="card-status generating">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>불러오는 중...</span>
              </div>
            ) : (
              <div className="card-status pending">
                <span>생성하기</span>
                <FontAwesomeIcon icon={faArrowRight} className="arrow" />
              </div>
            )}
          </div>
        </div>

        {/* 4. 조사 결과 분석하기 */}
        <div
          className="artifact-card"
          onClick={() => window.open('https://affinitybubble.com/', '_blank')}
        >
          <div className="card-icon">
            <IconSpecial name="resultsAnalysis" />
          </div>
          <div className="card-content">
            <h3>결과 분석하기</h3>
            <p className="card-description">
              pxdAX의 분석툴 어피니티버블로<br />
              조사 결과를 분석할 수 있습니다
            </p>
            <div className="card-status pending">
              <span>어피니티버블로 이동하기</span>
              <FontAwesomeIcon icon={faArrowRight} className="arrow" />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* 방법론 선택 모달 */}
      {methodologyModal.isOpen && (
        <div
          className="modal-overlay"
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>가이드라인 방법론 선택</h2>
            <p style={{ color: "#6b7280", marginBottom: "24px" }}>
              {methodologyModal.loading
                ? "계획서에서 방법론 추출 중..."
                : "어떤 방법론의 가이드라인을 생성하시겠습니까?"}
            </p>

            {methodologyModal.loading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  style={{ fontSize: "32px", color: "#7C9CE8" }}
                />
              </div>
            ) : (
              <>
                <div className="methodology-options">
                  {methodologyModal.extractedMethodologies.map((method) => {
                    const isSelected =
                      methodologyModal.selectedMethodologies.includes(method);

                    return (
                      <button
                        key={method}
                        className={`methodology-btn ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={() => {
                          setMethodologyModal((prev) => ({
                            ...prev,
                            selectedMethodologies: isSelected
                              ? prev.selectedMethodologies.filter(
                                  (m) => m !== method
                                )
                              : [...prev.selectedMethodologies, method],
                          }));
                        }}
                      >
                        {method}
                      </button>
                    );
                  })}
                </div>

                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() =>
                      setMethodologyModal({
                        isOpen: false,
                        extractedMethodologies: [],
                        selectedMethodologies: [],
                        loading: false,
                      })
                    }
                  >
                    취소
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleGenerateGuideline}
                    disabled={
                      methodologyModal.selectedMethodologies.length === 0
                    }
                  >
                    생성하기
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        message={alertModal.message}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        autoClose={alertModal.type === 'success'}
        autoCloseDelay={2000}
      />
      
      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        onClose={() => setUpgradeModal({ ...upgradeModal, isOpen: false })}
        reason={upgradeModal.reason}
      />
      <Toast
        isOpen={toast.isOpen}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast((current) => ({ ...current, isOpen: false }))}
      />
    </Layout>
  );
};

export default StudyWorkflowPage;
